import React, { useRef, useEffect } from 'react';
import './DayCard.css';
import { TaskItem } from './TaskItem';
import type { DaySchedule } from './scheduleData';
import { getWeekOfMonth } from './scheduleData';

interface DayCardProps {
  schedule: DaySchedule;
  tasks: Record<string, boolean>;
  onToggleTask: (id: string) => void;
  isToday: boolean;
  index: number;
  onTodayRef?: (el: HTMLDivElement | null) => void;
}

export const DayCard: React.FC<DayCardProps> = ({
  schedule, tasks, onToggleTask, isToday, index, onTodayRef,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const completedCount = schedule.tasks.filter((t) => tasks[t.id]).length;
  const totalCount = schedule.tasks.length;
  const isComplete = completedCount === totalCount;
  const currentWeek = getWeekOfMonth();

  useEffect(() => {
    if (isToday && onTodayRef && cardRef.current) {
      onTodayRef(cardRef.current);
    }
  }, [isToday, onTodayRef]);

  return (
    <div
      ref={cardRef}
      className={`day-card ${isToday ? 'day-card--today' : ''} ${isComplete ? 'day-card--complete' : ''}`}
      style={{
        '--day-color': schedule.color,
        '--day-light': schedule.lightColor,
        '--card-delay': `${index * 80}ms`,
      } as React.CSSProperties}
    >
      {isToday && <div className="day-card-today-indicator">Today</div>}
      {isComplete && (
        <div className="day-card-complete-badge">
          <svg viewBox="0 0 20 20" width="14" height="14">
            <circle cx="10" cy="10" r="9" fill="var(--day-color)" />
            <polyline points="6 10 9 13 14 7" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
      <div className="day-card-header">
        <h2 className="day-card-name">{schedule.day}</h2>
        <span className="day-card-theme">{schedule.theme}</span>
      </div>
      <div className="day-card-progress-track">
        <div className="day-card-progress-fill" style={{ width: `${(completedCount / totalCount) * 100}%` }} />
      </div>
      <div className="day-card-tasks">
        {schedule.tasks.map((task, i) => (
          <TaskItem
            key={task.id} id={task.id} label={task.label}
            checked={!!tasks[task.id]} color={schedule.color}
            onToggle={onToggleTask} weekNum={task.weekNum}
            isCurrentWeek={task.weekNum === currentWeek}
            animDelay={index * 80 + i * 50 + 200}
          />
        ))}
      </div>
      <div className="day-card-footer">
        <span className="day-card-count">{completedCount}/{totalCount}</span>
      </div>
    </div>
  );
};
